import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Tooltip, useTheme,
  ToggleButton, ToggleButtonGroup, LinearProgress, Dialog,
  DialogTitle, DialogContent, Chip, Divider, CircularProgress,
  Alert,
} from "@mui/material";
import { Refresh, Info, Close, CameraAlt } from "@mui/icons-material";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { motion } from "framer-motion";
import { getEnv } from "../helpers/env";
import Loading from "../components/Loading";
import {
  darkSurface,
  glassCard,
  heroText,
  pageBackground,
  motionVariants,
} from "../helpers/designSystem";

const AGENTS_URL =
  getEnv("REACT_APP_AGENTS_URL") ||
  "https://clue2app-agents.control.apps.clue2.app";

const COORDINATOR_URL =
  getEnv("REACT_APP_COORDINATOR_URL") ||
  "https://coordinator.control.apps.clue2.app";

const formatDollars = (amount) => {
  if (amount == null) return "$0.00";
  if (amount < 0.01) return `$${amount.toFixed(4)}`;
  if (amount < 1) return `$${amount.toFixed(3)}`;
  return `$${amount.toFixed(2)}`;
};

const formatCCU = (ccu) => {
  if (ccu == null) return "0";
  if (ccu < 1) return ccu.toFixed(3);
  return ccu.toFixed(2);
};

const cleanAppName = (displayName, serviceName) => {
  const name = displayName || serviceName || "";
  return name.replace(/^\d+-/, "").replace(/-image$/, "");
};

const shortenBuildName = (name) => {
  const match = name.match(/(?:\d+-)?(.+?)-image-build-(\d+)/);
  if (match) return `${match[1]}-${match[2]}`;
  return name.length > 20 ? name.slice(-20) : name;
};

const Costs = ({ currentUser }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const isDark = isDarkMode;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [costs, setCosts] = useState(null);
  const [buildCosts, setBuildCosts] = useState([]);
  const [trends, setTrends] = useState(null);
  const [summary, setSummary] = useState(null);
  const [trendPeriod, setTrendPeriod] = useState("daily");
  const [pricingModel, setPricingModel] = useState(null);
  const [showPricingModel, setShowPricingModel] = useState(false);

  const fetchCosts = useCallback(async () => {
    try {
      const response = await fetch(`${AGENTS_URL}/costs`);
      if (!response.ok) throw new Error("Failed to fetch cost data");
      const data = await response.json();
      setCosts(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBuildCosts = useCallback(async () => {
    try {
      const response = await fetch(`${COORDINATOR_URL}/api/build-costs/recent`);
      if (response.ok) setBuildCosts(await response.json());
    } catch { /* optional */ }
  }, []);

  const fetchTrends = useCallback(async (period) => {
    try {
      const response = await fetch(`${AGENTS_URL}/costs/trends?period=${period}&months=3`);
      if (response.ok) setTrends(await response.json());
    } catch { /* optional */ }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await fetch(`${AGENTS_URL}/costs/summary`);
      if (response.ok) setSummary(await response.json());
    } catch { /* optional */ }
  }, []);

  const fetchPricingModel = useCallback(async () => {
    try {
      const response = await fetch(`${AGENTS_URL}/costs/pricing-model`);
      if (response.ok) setPricingModel(await response.json());
    } catch { /* optional */ }
  }, []);

  useEffect(() => {
    fetchCosts();
    fetchBuildCosts();
    fetchTrends(trendPeriod);
    fetchSummary();
    fetchPricingModel();
    const interval = setInterval(fetchCosts, 30000);
    return () => clearInterval(interval);
  }, [fetchCosts, fetchBuildCosts, fetchTrends, fetchSummary, fetchPricingModel, trendPeriod]);

  const refreshAll = () => {
    setLoading(true);
    Promise.all([fetchCosts(), fetchBuildCosts(), fetchTrends(trendPeriod), fetchSummary()])
      .finally(() => setLoading(false));
  };

  const triggerSnapshot = async () => {
    try {
      await fetch(`${AGENTS_URL}/heal/costs`, { method: "POST" });
      setTimeout(fetchCosts, 2000);
    } catch (err) {
      setError(err.message);
    }
  };

  const chartSx = {
    '& .MuiChartsAxis-label': { fill: isDark ? '#94a3b8' : '#64748b', fontSize: 12 },
    '& .MuiChartsAxis-tickLabel': { fill: isDark ? '#94a3b8' : '#64748b' },
    '& .MuiChartsGrid-line': { stroke: isDark ? '#1e293b' : '#f1f5f9' },
    '& .MuiAreaElement-root': { fillOpacity: 0.08 },
    '& .MuiLineElement-root': { strokeWidth: 2 },
    '& .MuiMarkElement-root': { display: 'none' },
  };

  const panelSx = {
    ...glassCard(isDarkMode),
    p: 2,
    mb: 2,
  };

  const thSx = {
    fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary',
    borderColor: isDark ? '#1e293b' : undefined,
  };

  const tdSx = { borderColor: isDark ? '#1e293b' : undefined };

  const total = costs?.total || {};
  const projections = costs?.projections || {};
  const byApp = costs?.by_app || [];
  const byProject = costs?.by_project || [];
  const platformIdle = costs?.platform_idle || {};

  const pieData = byProject.map((proj, i) => ({
    id: i,
    value: proj.total_ccu || 0,
    label: proj.project_name,
    color: ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'][i % 6],
  }));

  const projectionPeriods = ["daily", "weekly", "monthly", "yearly"];
  const projectionData = projectionPeriods.map((p) => projections?.[p]?.dollars || 0);

  const succeededBuilds = buildCosts.filter((b) => b.status === "succeeded").slice(0, 10);

  const trendData = trends?.data || [];
  const mtd = summary?.mtd;
  const ytd = summary?.ytd;

  return (
    <Box sx={{ ...pageBackground(isDarkMode), py: 3 }}>
      <Box sx={{ px: 3, maxWidth: 1200, mx: 'auto' }}>
        {/* Hero */}
        <motion.div {...motionVariants.heroEnter}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h3" sx={{ ...heroText(isDarkMode), mb: 0.5 }}>
              Costs
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: isDarkMode ? darkSurface.textMuted : 'text.secondary' }}
            >
              What your apps are spending — and where.
            </Typography>
          </Box>
        </motion.div>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ flexGrow: 1 }}>
            Cost Dashboard
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
            {costs?.ccu_rate_per_hour ? `$${costs.ccu_rate_per_hour.toFixed(4)}/CCU/hr` : ''}
            {costs?.timestamp ? ` · ${new Date(costs.timestamp).toLocaleTimeString()}` : ''}
          </Typography>
          <Tooltip title="Cost Model">
            <IconButton size="small" onClick={() => setShowPricingModel(true)} sx={{ mr: 0.5 }}>
              <Info fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Snapshot">
            <IconButton size="small" onClick={triggerSnapshot} sx={{ mr: 0.5 }}>
              <CameraAlt fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={refreshAll}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Loading />
      ) : (
        <>
          {/* Summary Stats */}
          <Box sx={{ display: 'flex', gap: 4, mb: 3, flexWrap: 'wrap' }}>
            {[
              { label: 'Total CCU', value: formatCCU(total.ccu), color: '#F97316' },
              { label: '$/Hour', value: formatDollars(total.ccu_dollars_per_hour), color: '#10b981' },
              { label: '$/Month', value: formatDollars(total.ccu_dollars_per_month), color: '#8b5cf6' },
              { label: 'Apps', value: total.apps_count || 0, color: '#06b6d4' },
              { label: 'Pods', value: total.pods_count || 0, color: '#94a3b8' },
            ].map(stat => (
              <Box key={stat.label} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography variant="h5" fontWeight={700} sx={{ color: stat.color }}>
                  {stat.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* MTD / YTD */}
          {(mtd || ytd) && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
              {mtd && (
                <Box sx={panelSx}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                      Month to Date
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Day {mtd.days_elapsed} of {mtd.days_elapsed + (mtd.days_remaining || 0)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'baseline' }}>
                    <Box>
                      <Typography variant="h5" fontWeight={700} sx={{ color: '#10b981' }}>
                        {formatDollars(mtd.dollars)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">spent</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="text.secondary">
                        {formatDollars(mtd.projected_total)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">projected</Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (mtd.days_elapsed / (mtd.days_elapsed + (mtd.days_remaining || 1))) * 100)}
                    sx={{ mt: 1.5, height: 4, borderRadius: 2, bgcolor: isDark ? '#1e293b' : '#f1f5f9',
                      '& .MuiLinearProgress-bar': { bgcolor: '#F97316', borderRadius: 2 } }}
                  />
                </Box>
              )}
              {ytd && (
                <Box sx={panelSx}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                      Year to Date
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Day {ytd.days_elapsed} of {ytd.days_elapsed + (ytd.days_remaining || 0)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'baseline' }}>
                    <Box>
                      <Typography variant="h5" fontWeight={700} sx={{ color: '#8b5cf6' }}>
                        {formatDollars(ytd.dollars)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">spent</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="text.secondary">
                        {formatDollars(ytd.projected_total)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">projected</Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (ytd.days_elapsed / (ytd.days_elapsed + (ytd.days_remaining || 1))) * 100)}
                    sx={{ mt: 1.5, height: 4, borderRadius: 2, bgcolor: isDark ? '#1e293b' : '#f1f5f9',
                      '& .MuiLinearProgress-bar': { bgcolor: '#8b5cf6', borderRadius: 2 } }}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Pie + Projections side by side */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
            {pieData.length > 0 && (
              <Box sx={panelSx}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  CCU by Project
                </Typography>
                <PieChart
                  series={[{
                    data: pieData,
                    innerRadius: 35,
                    paddingAngle: 2,
                    cornerRadius: 3,
                    highlightScope: { faded: "global", highlighted: "item" },
                  }]}
                  height={220}
                  slotProps={{
                    legend: {
                      direction: "column",
                      position: { vertical: "middle", horizontal: "right" },
                      itemMarkWidth: 8,
                      itemMarkHeight: 8,
                      labelStyle: { fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' },
                    },
                  }}
                  sx={chartSx}
                />
              </Box>
            )}

            <Box sx={panelSx}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                Cost Projections
              </Typography>
              <BarChart
                xAxis={[{ scaleType: "band", data: ["Daily", "Weekly", "Monthly", "Yearly"] }]}
                series={[{
                  data: projectionData,
                  color: '#10b981',
                  valueFormatter: (v) => formatDollars(v),
                }]}
                height={220}
                grid={{ horizontal: true }}
                slotProps={{ legend: { hidden: true } }}
                sx={chartSx}
              />
            </Box>
          </Box>

          {/* Cost Trend */}
          {trendData.length > 0 && (
            <Box sx={panelSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  Cost Trend
                </Typography>
                <ToggleButtonGroup
                  value={trendPeriod}
                  exclusive
                  onChange={(_, v) => v && setTrendPeriod(v)}
                  size="small"
                >
                  {['daily', 'weekly', 'monthly'].map(p => (
                    <ToggleButton key={p} value={p} sx={{ px: 1.5, py: 0.3, fontSize: '0.7rem', textTransform: 'capitalize' }}>
                      {p}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
              <LineChart
                xAxis={[{
                  scaleType: "band",
                  data: trendData.map((d) => d.date),
                  tickLabelStyle: { fontSize: 10, angle: -30 },
                }]}
                series={[{
                  data: trendData.map((d) => d.dollars),
                  color: '#10b981',
                  area: true,
                  showMark: false,
                  valueFormatter: (v) => formatDollars(v),
                }]}
                height={200}
                grid={{ horizontal: true }}
                slotProps={{ legend: { hidden: true } }}
                sx={chartSx}
              />
            </Box>
          )}

          {/* Per-App Breakdown */}
          {byApp.length > 0 && (
            <Paper sx={{ ...panelSx, p: 0, overflow: 'hidden' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5} sx={{ px: 2, pt: 2, display: 'block' }}>
                Cost by Application
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thSx}>App</TableCell>
                      <TableCell align="center" sx={thSx}>Tier</TableCell>
                      <TableCell align="right" sx={thSx}>CCU</TableCell>
                      <TableCell align="right" sx={thSx}>$/hour</TableCell>
                      <TableCell align="right" sx={thSx}>$/month</TableCell>
                      <TableCell align="right" sx={thSx}>Pods</TableCell>
                      <TableCell sx={thSx}>Project</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byApp.map((app) => {
                      const pct = total.ccu > 0 ? ((app.ccu / total.ccu) * 100).toFixed(1) : 0;
                      return (
                        <TableRow key={app.service_name} hover>
                          <TableCell sx={tdSx}>
                            <Typography variant="body2" fontWeight={500}>
                              {cleanAppName(app.display_name, app.service_name)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                              <LinearProgress
                                variant="determinate"
                                value={parseFloat(pct)}
                                sx={{ width: 50, height: 3, borderRadius: 2, bgcolor: isDark ? '#1e293b' : '#f1f5f9',
                                  '& .MuiLinearProgress-bar': { bgcolor: '#F97316', borderRadius: 2 } }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                {pct}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={tdSx}>
                            <Chip size="small" label={app.tier || "SMALL"} variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 20,
                                borderColor: isDark ? '#334155' : '#e2e8f0',
                                color: 'text.secondary' }} />
                          </TableCell>
                          <TableCell align="right" sx={tdSx}>
                            <Typography variant="body2" fontWeight={500} sx={{ color: '#F97316' }}>
                              {formatCCU(app.tier_ccu || app.ccu)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={tdSx}>
                            <Typography variant="body2">{formatDollars(app.ccu_dollars_per_hour)}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={tdSx}>
                            <Typography variant="body2" fontWeight={500} sx={{ color: '#10b981' }}>
                              {formatDollars(app.ccu_dollars_per_month)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={tdSx}>
                            <Typography variant="body2" color="text.secondary">{app.pods}</Typography>
                          </TableCell>
                          <TableCell sx={tdSx}>
                            <Typography variant="caption" color="text.secondary">{app.project_name}</Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Per-Project Breakdown */}
          {byProject.length > 0 && (
            <Paper sx={{ ...panelSx, p: 0, overflow: 'hidden' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5} sx={{ px: 2, pt: 2, display: 'block' }}>
                Cost by Project
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thSx}>Project</TableCell>
                      <TableCell align="right" sx={thSx}>Apps</TableCell>
                      <TableCell align="right" sx={thSx}>CCU</TableCell>
                      <TableCell align="right" sx={thSx}>$/hr</TableCell>
                      <TableCell align="right" sx={thSx}>$/month</TableCell>
                      <TableCell align="right" sx={thSx}>Share</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byProject.map((proj) => {
                      const pct = total.ccu > 0 ? ((proj.total_ccu / total.ccu) * 100).toFixed(1) : 0;
                      return (
                        <TableRow key={proj.project_id || proj.project_name} hover>
                          <TableCell sx={tdSx}>
                            <Typography variant="body2" fontWeight={500}>{proj.project_name}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={tdSx}>
                            <Typography variant="body2" color="text.secondary">{proj.apps_count}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={tdSx}>
                            <Typography variant="body2" fontWeight={500} sx={{ color: '#F97316' }}>
                              {formatCCU(proj.total_ccu)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={tdSx}>
                            <Typography variant="body2">{formatDollars(proj.ccu_dollars_per_hour)}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={tdSx}>
                            <Typography variant="body2" fontWeight={500} sx={{ color: '#10b981' }}>
                              {formatDollars(proj.ccu_dollars_per_month)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={tdSx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                              <LinearProgress
                                variant="determinate"
                                value={parseFloat(pct)}
                                sx={{ width: 50, height: 3, borderRadius: 2, bgcolor: isDark ? '#1e293b' : '#f1f5f9',
                                  '& .MuiLinearProgress-bar': { bgcolor: '#8b5cf6', borderRadius: 2 } }}
                              />
                              <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.75rem' }}>
                                {pct}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Recent Build Costs */}
          {succeededBuilds.length > 0 && (
            <Box sx={panelSx}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                Recent Build Costs
              </Typography>
              <BarChart
                xAxis={[{
                  scaleType: "band",
                  data: succeededBuilds.map((b) => {
                    const name = shortenBuildName(b.buildName);
                    const m = name.match(/(.+)-(\d+)$/);
                    return m ? `${m[1].slice(0, 12)} #${m[2]}` : name.slice(0, 15);
                  }),
                  tickLabelStyle: { fontSize: 9, angle: -40 },
                }]}
                yAxis={[{
                  label: "Cost (¢)",
                  valueFormatter: (v) => `${(v * 100).toFixed(1)}¢`,
                }]}
                series={[{
                  data: succeededBuilds.map((b) => b.costDollars || 0),
                  color: '#06b6d4',
                  valueFormatter: (v) => v < 0.01 ? `${(v * 100).toFixed(2)}¢` : formatDollars(v),
                }]}
                height={200}
                grid={{ horizontal: true }}
                slotProps={{ legend: { hidden: true } }}
                margin={{ bottom: 55 }}
                sx={chartSx}
              />

              <TableContainer sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thSx}>Build</TableCell>
                      <TableCell align="right" sx={thSx}>Duration</TableCell>
                      <TableCell align="right" sx={thSx}>CCU</TableCell>
                      <TableCell align="right" sx={thSx}>Cost</TableCell>
                      <TableCell align="right" sx={thSx}>Completed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {succeededBuilds.map((build) => (
                      <TableRow key={build.buildName} hover>
                        <TableCell sx={tdSx}>
                          <Typography variant="body2" fontWeight={500}>
                            {shortenBuildName(build.buildName)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            {build.imageName}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={tdSx}>
                          <Typography variant="body2" color="text.secondary">
                            {build.durationSeconds ? `${Math.floor(build.durationSeconds / 60)}m ${build.durationSeconds % 60}s` : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={tdSx}>
                          <Typography variant="body2" fontWeight={500} sx={{ color: '#F97316' }}>
                            {formatCCU(build.ccu)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={tdSx}>
                          <Typography variant="body2" fontWeight={500} sx={{ color: '#10b981' }}>
                            {formatDollars(build.costDollars)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={tdSx}>
                          <Typography variant="caption" color="text.secondary">
                            {build.buildCompletedAt ? new Date(build.buildCompletedAt).toLocaleString() : "-"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Scaled to Zero */}
          {platformIdle?.scaled_to_zero_count > 0 && (
            <Box sx={{ ...panelSx, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {platformIdle.scaled_to_zero_count} services scaled to zero — no cost
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Cost Model Dialog */}
      <Dialog open={showPricingModel} onClose={() => setShowPricingModel(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, bgcolor: isDark ? '#0f172a' : '#fff' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>Cost Model</Typography>
            <IconButton onClick={() => setShowPricingModel(false)} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {pricingModel ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {pricingModel.description}
              </Typography>

              <Box sx={{ ...panelSx, mb: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  CCU (Clue Currency Unit)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {pricingModel.ccu_unit}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Rate: <strong style={{ color: '#10b981' }}>${pricingModel.ccu_rate_per_hour?.toFixed(4)}/CCU/hr</strong>
                </Typography>
              </Box>

              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                Application Size Tiers
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, mt: 0.5 }}>
                Default: <Chip size="small" label={pricingModel.default_tier} variant="outlined"
                  sx={{ fontSize: '0.65rem', height: 18 }} />
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thSx}>Tier</TableCell>
                      <TableCell align="right" sx={thSx}>CPU</TableCell>
                      <TableCell align="right" sx={thSx}>Memory</TableCell>
                      <TableCell align="right" sx={thSx}>CCU</TableCell>
                      <TableCell align="right" sx={thSx}>$/month</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(pricingModel.tiers || []).map((tier) => (
                      <TableRow key={tier.name} selected={tier.is_default}>
                        <TableCell sx={tdSx}>
                          <Chip size="small" label={tier.name}
                            variant={tier.is_default ? "filled" : "outlined"}
                            color={tier.is_default ? "primary" : "default"}
                            sx={{ fontSize: '0.65rem', height: 20 }} />
                        </TableCell>
                        <TableCell align="right" sx={tdSx}>
                          <Typography variant="body2">{tier.resources?.cpu}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={tdSx}>
                          <Typography variant="body2">{tier.resources?.memory}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={tdSx}>
                          <Typography variant="body2" fontWeight={500} sx={{ color: '#F97316' }}>
                            {tier.ccu}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={tdSx}>
                          <Typography variant="body2" fontWeight={500} sx={{ color: '#10b981' }}>
                            {formatDollars(tier.price_per_month)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: isDark ? '#1e293b' : '#f8fafc' }}>
                <Typography variant="caption" color="text.secondary">
                  Apps are billed at their tier's fixed CCU rate. Monthly cost is predictable —
                  it only changes when you change your app's tier.
                </Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </DialogContent>
      </Dialog>
      </Box>
    </Box>
  );
};

export default Costs;
